import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Paper,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import QRCode from 'react-qr-code';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useSnackbar } from 'notistack';

interface QRPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  qrCode: string | null;
  paymentLink: string | null;
  facturaNumero: number;
  loading?: boolean;
  isPrototype?: boolean;
}

const QRPaymentDialog: React.FC<QRPaymentDialogProps> = ({
  open,
  onClose,
  qrCode,
  paymentLink,
  facturaNumero,
  loading = false,
  isPrototype = false,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const handleCopyLink = () => {
    if (paymentLink) {
      navigator.clipboard.writeText(paymentLink);
      enqueueSnackbar('Link copiado al portapapeles', { variant: 'success' });
    }
  };

  const handleOpenLink = () => {
    if (paymentLink) {
      window.open(paymentLink, '_blank');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Pago con Mercado Pago - Factura #{facturaNumero}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Generando link de pago...
            </Typography>
          </Box>
        ) : paymentLink ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            {isPrototype && (
              <Alert severity="info" sx={{ width: '100%' }}>
                Vista previa: este QR y el link son de demostración hasta que Mercado Pago esté configurado.
              </Alert>
            )}
            <Typography variant="body1" align="center" color="text.secondary">
              Escanea el código QR con tu celular para realizar el pago
            </Typography>
            
            <Paper
              elevation={3}
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: 'white',
                borderRadius: 2,
              }}
            >
              <QRCode
                value={paymentLink}
                size={256}
                level="H"
                bgColor="#FFFFFF"
                fgColor="#000000"
              />
            </Paper>

            <Box sx={{ width: '100%', mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                O copia el siguiente link:
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'grey.50',
                  wordBreak: 'break-all',
                }}
              >
                <Typography variant="body2" sx={{ flex: 1, fontSize: '0.75rem' }}>
                  {paymentLink}
                </Typography>
                <Tooltip title="Copiar link">
                  <IconButton onClick={handleCopyLink} size="small" color="primary">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Abrir en nueva pestaña">
                  <IconButton onClick={handleOpenLink} size="small" color="primary">
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Paper>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="error">
              No se pudo generar el link de pago
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cerrar
        </Button>
        {paymentLink && (
          <Button onClick={handleOpenLink} variant="contained" color="primary" startIcon={<OpenInNewIcon />}>
            Abrir link de pago
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QRPaymentDialog;

